#!/usr/bin/env node

import { appendFileSync, readFileSync, writeFileSync, existsSync } from 'fs'
import { join } from 'path'
import { homedir } from 'os'
import { spawn } from 'child_process'
import { fileURLToPath } from 'url'
import { dirname } from 'path'

// Parse command line arguments
const args = process.argv.slice(2)
const intervalIndex = args.indexOf('--interval')
const apiKeyIndex = args.indexOf('--api-key')

const interval = intervalIndex !== -1 ? parseInt(args[intervalIndex + 1]) : 5
const apiKey = apiKeyIndex !== -1 ? args[apiKeyIndex + 1] : ''

// Log file path
const LOG_FILE = join(homedir(), '.vibetime', 'daemon.log')
const STATE_FILE = join(homedir(), '.vibetime', 'sync-state.json')

// Get current directory
const __filename = fileURLToPath(import.meta.url)
const __dirname = dirname(__filename)

// State management for tracking last sync
interface SyncState {
  lastSyncTime: string
  lastSyncDate: string  // YYYY-MM-DD format for tracking complete days
  lastSyncCount: number
}

function loadSyncState(): SyncState {
  if (existsSync(STATE_FILE)) {
    try {
      const data = readFileSync(STATE_FILE, 'utf-8')
      return JSON.parse(data)
    } catch (error) {
      log(`Warning: Could not load sync state: ${error}`)
    }
  }
  
  // Default state - sync data from last 7 days on first run
  const lastSyncTime = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  return {
    lastSyncTime: lastSyncTime.toISOString(),
    lastSyncDate: lastSyncTime.toISOString().split('T')[0],
    lastSyncCount: 0
  }
}

function saveSyncState(state: SyncState): void {
  try {
    writeFileSync(STATE_FILE, JSON.stringify(state, null, 2))
  } catch (error) {
    log(`Warning: Could not save sync state: ${error}`)
  }
}

function log(message: string) {
  const timestamp = new Date().toISOString()
  const logMessage = `[${timestamp}] ${message}\n`
  appendFileSync(LOG_FILE, logMessage)
  console.log(logMessage.trim())
}

async function runSync(): Promise<void> {
  try {
    log('Starting automatic sync...')
    
    // Load current sync state
    const state = loadSyncState()
    const lastSyncDate = state.lastSyncDate
    
    // Calculate date range for sync
    // Sync from last sync date to yesterday (to ensure complete days)
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    yesterday.setHours(0, 0, 0, 0)
    const yesterdayStr = yesterday.toISOString().split('T')[0]
    
    // Don't sync if we're already up to date
    if (lastSyncDate >= yesterdayStr) {
      log(`Already synced up to ${lastSyncDate}, nothing to do`)
      return
    }
    
    log(`Syncing from ${lastSyncDate} to ${yesterdayStr}`)
    
    // Use the CLI sync command with state-aware timing
    const vibetimePath = join(__dirname, 'index.js')
    
    return new Promise((resolve, reject) => {
      const sync = spawn(process.execPath, [
        vibetimePath,
        'sync',
        '--api-key', apiKey,
        '--since', lastSyncDate,
        '--until', yesterdayStr
      ], {
        stdio: ['ignore', 'pipe', 'pipe']
      })

      let output = ''
      let errorOutput = ''

      sync.stdout.on('data', (data: Buffer) => {
        output += data.toString()
      })

      sync.stderr.on('data', (data: Buffer) => {
        errorOutput += data.toString()
      })

      sync.on('close', (code) => {
        if (code === 0) {
          // Extract sync count from success message
          const countMatch = output.match(/Successfully (?:synced|upserted) (\d+) (?:aggregates|days)/)
          const syncCount = countMatch ? parseInt(countMatch[1]) : 0
          
          if (syncCount > 0 || output.includes('No usage data found')) {
            if (syncCount > 0) {
              log(`Sync completed: ${syncCount} aggregates synced`)
            } else {
              log('Sync completed: No new data to sync')
            }
            
            // Update sync state to yesterday (last complete day)
            const newState: SyncState = {
              lastSyncTime: new Date().toISOString(),
              lastSyncDate: yesterdayStr,
              lastSyncCount: syncCount
            }
            saveSyncState(newState)
          }
          
          resolve()
        } else {
          log(`Sync failed with code ${code}: ${errorOutput || output}`)
          reject(new Error(`Sync failed with code ${code}`))
        }
      })

      sync.on('error', (error) => {
        log(`Sync error: ${error.message}`)
        reject(error)
      })
    })
  } catch (error) {
    log(`Sync setup error: ${error}`)
    throw error
  }
}

async function daemon() {
  log(`Daemon started with PID ${process.pid}`)
  log(`Sync interval: ${interval} minutes`)
  log(`API Key: ${apiKey.substring(0, 10)}...`)

  // Initial run
  try {
    await runSync()
  } catch (error) {
    log(`Initial sync failed: ${error}`)
  }

  // Scheduled runs
  const intervalMs = interval * 60 * 1000
  setInterval(async () => {
    try {
      await runSync()
    } catch (error) {
      log(`Scheduled sync failed: ${error}`)
    }
  }, intervalMs)

  // Signal handling
  process.on('SIGTERM', () => {
    log('Received SIGTERM, shutting down...')
    process.exit(0)
  })

  process.on('SIGINT', () => {
    log('Received SIGINT, shutting down...')
    process.exit(0)
  })

  // Error handling
  process.on('uncaughtException', (error) => {
    log(`Uncaught exception: ${error.message}`)
    process.exit(1)
  })

  process.on('unhandledRejection', (reason) => {
    log(`Unhandled rejection: ${reason}`)
    process.exit(1)
  })
}

// Start daemon
daemon().catch((error) => {
  log(`Daemon startup failed: ${error.message}`)
  process.exit(1)
})